import _ from 'underscore';

import { filePathDisplayOnlyFileName } from '~src/spacesPlatform/utils/FilePathUtils';

import { IDataConnection } from '../../../datasource/dataConnection/serverApi/types/IDataConnection';
import { IDataSource } from '../../../datasource/ServerApi/types/IDataSource';
import { FlowOutputStep } from '../../serverApi/types/FlowOutputStep';
import { FlowRunSpecOutputStepParamsMap } from '../../serverApi/types/RunFlowTask';
import { DatasourceInfoForFlowOutput } from '../DatasourceInfoForFlowOutput';
import { FlowOutputStepInfo } from '../FlowOutputStepInfo';

export module FlowOutputStepMapper {

  export function map(
    flowOutputSteps: ReadonlyArray<FlowOutputStep>,
    dataConnections: _.Dictionary<IDataConnection>,
    datasources: _.Dictionary<IDataSource>,
    flowRunSpecOutputStepParamsMap: FlowRunSpecOutputStepParamsMap
  ): FlowOutputStepInfo[] {

    return _.map(flowOutputSteps, (fos: FlowOutputStep) => {
      const baseDataConnection = dataConnections[fos.connectionId];
      const connectedDatasources: DatasourceInfoForFlowOutput[] = [];
      const connectedMap: {[key: string]: boolean} = {};
      fos.connectionIds.forEach(connection => {
        if (!connection.datasourceId) {
          // if there is no datasourceId, check if it is a file connection
          const dataConnection = dataConnections[connection.connectionId];
          if (dataConnection?.connectionDetails.type === 'file') {
            connectedDatasources.push(createDatasourceInfoForFlowOutput(
              filePathDisplayOnlyFileName(dataConnection.connectionDetails.fileName),
              dataConnection,
              dataConnection.id
            ));
          }
        } else {
          const dataConnection = dataConnections[connection.connectionId];
          const datasource = datasources[connection.connectionId];
          if (!connectedMap[connection.datasourceId]) {
            connectedMap[connection.datasourceId] = true;
            connectedDatasources.push(createDatasourceInfoForFlowOutput(datasource.name, dataConnection, connection.datasourceId));
          }
        }
      });
      return {
        dataConnection: baseDataConnection,
        flowId: fos.flowId,
        id: fos.id,
        name: fos.name,
        datasourceName: baseDataConnection.datasourceId && datasources[baseDataConnection.datasourceId].name || '',
        datasourceId: fos.datasourceId,
        dataRoleId: fos.dataRoleId,
        isIncremental: getIsIncremental(flowRunSpecOutputStepParamsMap, fos.id),
        isIncrementalEnabled: fos.isIncrementalEnabled,
        isIncrementalDefault: fos.isIncrementalDefault,
        outputOperationType: fos.outputOperationType,
        incrementalOutputOperationType: fos.incrementalOutputOperationType,
        relevantParameters: fos.relevantParameters,
        connectedDatasources,
      };
    });
  }

  function createDatasourceInfoForFlowOutput(
    name: string,
    dataConnection: IDataConnection,
    id: string,
  ): DatasourceInfoForFlowOutput {
    return {
      connectionDetails: dataConnection.connectionDetails,
      connectionType: dataConnection.connectionType,
      id: id,
      name: name,
    };
  }

  export function mapForRunFlowTask(
    flowOutputSteps: FlowOutputStep[],
    dataConnections: _.Dictionary<IDataConnection>,
    flowRunSpecOutputStepParamsMap: FlowRunSpecOutputStepParamsMap,
    isRunAllIncremental: boolean,
    datasources?: _.Dictionary<IDataSource>,
  ): FlowOutputStepInfo[] {

    return _.map(flowOutputSteps, (fos: FlowOutputStep) => {
      const dataConnection = dataConnections[fos.connectionId];
      const datasource = datasources !== undefined  ? datasources[dataConnection.datasourceId] : undefined;
      const datasourceName = datasource ? datasource.name || '' : undefined;
      const isIncremental = isRunAllIncremental || getIsIncremental(flowRunSpecOutputStepParamsMap, fos.id);
      return {
        dataConnection: dataConnection,
        flowId: fos.flowId,
        id: fos.id,
        name: fos.name,
        datasourceName: datasourceName,
        datasourceId: fos.datasourceId,
        dataRoleId: fos.dataRoleId,
        isIncremental: isIncremental,
        isIncrementalEnabled: fos.isIncrementalEnabled,
        isIncrementalDefault: fos.isIncrementalDefault,
        outputOperationType: fos.outputOperationType,
        incrementalOutputOperationType: fos.incrementalOutputOperationType,
        relevantParameters: fos.relevantParameters,
      };
    });
  }

  function getIsIncremental(flowRunSpecOutputStepParamsMap: FlowRunSpecOutputStepParamsMap, id: string) {
    if (!flowRunSpecOutputStepParamsMap){
      return null;
    }

    const param = flowRunSpecOutputStepParamsMap[parseInt(id, 10)];

    return param ? param.isIncremental : null;
  }
}
